import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, FileText, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Brand } from "@/components/Layout";
import { Panel, inputCls, itDate } from "@/components/kit";
import { API, api, errMsg, fmtMoney } from "@/lib/api";
import { GalleryStrip } from "@/components/Gallery";
import { LinkHero } from "@/components/LinkHero";
import { PackageCards } from "@/components/Packages";

const n = (v) => Number(v || 0);

export default function PublicQuote() {
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState("");
  const [nome, setNome] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [chosen, setChosen] = useState(-1);
  const load = () => api.get(`/public/quote/${token}`).then((r) => setQ(r.data)).catch((e) => setErr(errMsg(e)));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);
  const respond = async (accept) => {
    setBusy(true);
    try {
      await api.post(`/public/quote/${token}/respond`, { accept, nome, note, pacchetto: chosen });
      toast.success(accept ? "Grazie! Abbiamo ricevuto la sua conferma" : "Risposta inviata");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  if (err) return <div className="min-h-screen flex items-center justify-center p-6 text-center" data-testid="public-quote-error"><div><Brand /><p className="mt-6 text-[#47606B]">{err}</p></div></div>;
  if (!q) return <div className="p-10 text-sm text-[#7B929C]">Caricamento…</div>;
  const answered = q.status !== "inviato";
  const pk = q.pacchetti.length > 0;
  return (
    <div className="min-h-screen pb-16" data-testid="public-quote">
      <header className="relative overflow-hidden bg-[#0D2B3A]">
        <img src="/img/mare.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 py-10 space-y-6 text-white">
          <div className="inline-block rounded-xl bg-white/90 px-4 py-3"><Brand small /></div>
          <div className="max-w-2xl"><LinkHero kind="preventivo" token={token} dark /></div>
          <div className="fade-up">
            <div className="text-xs uppercase tracking-[0.3em] text-white/70">Proposta n. {q.number}</div>
            <h1 className="font-serif-it text-4xl sm:text-5xl lg:text-6xl font-semibold mt-2">{q.evento_tipo} di {q.cliente_nome}</h1>
            <div className="mt-2 text-white/80 text-sm">{q.evento_data ? itDate(q.evento_data) : "data da definire"} • {q.sede || "sede da definire"}</div>
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 -mt-4 relative space-y-6">
        <GalleryStrip items={q.gallery} sede={q.sede} />
        {pk && <Panel title={answered ? "Le proposte" : "Scelga la proposta che preferisce"} testId="public-quote-packages"><PackageCards q={q} chosen={chosen} setChosen={setChosen} locked={answered} /></Panel>}
        {!pk && <Panel title={`Il menù${q.formula ? ` • ${q.formula}` : ""}`} testId="public-quote-menu">
          {q.menu.length === 0 && <div className="text-sm text-[#7B929C]">Menù da definire insieme.</div>}
          {q.menu.map((m) => <div key={m.label} className="text-sm"><div className="text-xs text-[#7B929C]">{m.label}</div><div className="whitespace-pre-line">{m.value}</div></div>)}
        </Panel>}
        <Panel title={pk ? "Servizi compresi in tutte le proposte" : "Riepilogo economico"} testId="public-quote-totals">
          <div className="text-sm space-y-2">
            {!pk && n(q.adulti) > 0 && <div className="flex justify-between"><span>Menù adulti • {q.adulti} x {fmtMoney(q.prezzo_adulti)}</span><span>{fmtMoney(n(q.adulti) * n(q.prezzo_adulti))}</span></div>}
            {!pk && n(q.bambini) > 0 && <div className="flex justify-between"><span>Menù bambini • {q.bambini} x {fmtMoney(q.prezzo_bambini)}</span><span>{fmtMoney(n(q.bambini) * n(q.prezzo_bambini))}</span></div>}
            {q.items.map((i, k) => <div key={k} className="flex justify-between"><span>{i.servizio} • {n(i.quantita)} x {fmtMoney(i.unitario)}</span><span>{fmtMoney(n(i.quantita) * n(i.unitario))}</span></div>)}
            {!pk && q.totals.sconto > 0 && <div className="flex justify-between border-t border-[#D4E6EC] pt-2"><span>Subtotale</span><span>{fmtMoney(q.totals.subtotale)}</span></div>}
            {!pk && q.totals.sconto > 0 && <div className="flex justify-between text-[#166534] font-medium" data-testid="public-quote-discount"><span>{q.totals.sconto_label}</span><span>- {fmtMoney(q.totals.sconto)}</span></div>}
            {pk && !q.items.length && <div className="text-[#7B929C]">Il totale di ogni proposta è indicato qui sopra.</div>}
            {!pk && <div className="flex justify-between border-t border-[#D4E6EC] pt-3 font-serif-it text-3xl font-semibold"><span>Totale</span><span data-testid="public-quote-total">{fmtMoney(q.totals.totale)}</span></div>}
          </div>
          {q.note && <p className="text-sm text-[#47606B] whitespace-pre-line">{q.note}</p>}
          <a href={`${API}/public/quote/${token}/pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-[#0B6E8F] hover:underline" data-testid="public-quote-pdf"><FileText size={15} /> Scarica il PDF</a>
        </Panel>
        {q.premio && (
          <div className="rounded-xl border border-[#E9D7A8] bg-[#FBF6EA] p-5 flex items-center gap-4 fade-up" data-testid="public-quote-prize">
            <div className="text-3xl">🎁</div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#A88B5C]">Il suo premio</div>
              <div className="font-serif-it text-2xl font-semibold text-[#0D2B3A]">{q.premio.nome}</div>
              <div className="text-xs text-[#47606B]">Valido se conferma l'evento firmando il contratto entro il {itDate(q.premio.scade)}</div>
            </div>
          </div>
        )}
        <Panel title={answered ? "Grazie per la risposta" : "La sua risposta"} testId="public-quote-respond">
          {answered ? <p className="text-sm" data-testid="public-quote-answered">{["accettato", "convertito"].includes(q.status) ? "Proposta confermata 🎉 A breve la contatteremo per il contratto e la firma." : "Abbiamo ricevuto la sua risposta. Restiamo a disposizione per una nuova proposta."}</p> : q.expired ? <p className="text-sm text-[#92400E]">La proposta era valida fino al {itDate(q.valid_until)}: ci contatti per aggiornarla.</p> : (
            <div className="space-y-3">
              <p className="text-xs text-[#7B929C]">Proposta valida fino al {itDate(q.valid_until)}. Confermando, la data viene riservata dopo la firma del contratto e il versamento della prima rata.</p>
              <input className={inputCls} value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome e cognome" data-testid="public-quote-name" />
              <textarea rows={2} className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note o richieste (facoltativo)" data-testid="public-quote-note" />
              <div className="flex flex-wrap gap-2">
                <Button className="rounded-full bg-[#2A9D8F] hover:bg-[#21867A]" disabled={busy || nome.trim().length < 3 || (pk && chosen < 0)} onClick={() => respond(true)} data-testid="public-quote-accept"><Check size={15} className="mr-2" /> {pk ? (chosen >= 0 ? `Accetto la proposta ${q.pacchetti[chosen].nome}` : "Scelga prima una proposta") : "Accetto la proposta"}</Button>
                <Button variant="ghost" className="rounded-full" disabled={busy || nome.trim().length < 3} onClick={() => respond(false)} data-testid="public-quote-reject"><X size={15} className="mr-2" /> Non accetto</Button>
              </div>
            </div>
          )}
        </Panel>
      </main>
    </div>
  );
}
